import React, { useState, useEffect, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import apiClient from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";
import NumericInput from "./ui/NumericInput";
import CompanySelect from "./ui/CompanySelect";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";


// ── Constants ─────────────────────────────────────────────────────────────────

const ASSET_TYPES = [
  { value: "STOCK",               label: 'מניות' },
  { value: "REAL_ESTATE",         label: 'נדל"ן מניב' },
  { value: "LAND",                label: "קרקע" },
  { value: "DEVELOPMENT_PROJECT", label: "פרויקט בפיתוח" },
  { value: "LOAN_RECEIVABLE",     label: "הלוואה שניתנה" },
  { value: "PRIVATE_EQUITY",      label: "הון פרטי" },
  { value: "CASH",                label: "מזומן ושווי מזומן" },
  { value: "OTHER",               label: "אחר" },
];

const LIABILITY_TYPES = [
  { value: "LAWSUIT",       label: "תביעה משפטית" },
  { value: "TRADE_PAYABLE", label: "חוב לספקים" },
  { value: "TAX_LIABILITY", label: "התחייבות מס" },
  { value: "OTHER",         label: "אחר" },
];

const STATUS_OPTIONS = [
  { value: "OPEN",    label: "פתוח" },
  { value: "SETTLED", label: "נסגר" },
];

const DIV_FREQ_LABELS = {
  MONTHLY: "חודשי", QUARTERLY: "רבעוני",
  SEMI_ANNUALLY: "חצי שנתי", ANNUALLY: "שנתי",
};

const TYPE_COLORS = {
  STOCK: "#2563eb", REAL_ESTATE: "#7c3aed", LAND: "#059669",
  DEVELOPMENT_PROJECT: "#d97706", LOAN_RECEIVABLE: "#0891b2",
  PRIVATE_EQUITY: "#be185d", CASH: "#16a34a", OTHER: "#6b7280",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(v) {
  if (v == null) return "—";
  const abs = Math.abs(v);
  let s;
  if (abs >= 1_000_000_000)     s = `${(abs / 1_000_000_000).toFixed(2)}B`;
  else if (abs >= 1_000_000)    s = `${(abs / 1_000_000).toFixed(1)}M`;
  else                           s = abs.toLocaleString("he-IL");
  return `${v < 0 ? "-" : ""}₪${s}`;
}
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("he-IL") : "—"; }
function fmtPct(v)  { return v != null ? `${Number(v).toFixed(1)}%` : "—"; }

// ── Asset Form Modal ──────────────────────────────────────────────────────────

const EMPTY_ASSET = {
  assetType: "STOCK", assetName: "", currency: "ILS", notes: "",
  // STOCK fields
  totalUnits: "", symbol: "", minimumFreeRequired: "", minimumFreeReason: "",
  dividendPerShare: "", dividendFrequency: "QUARTERLY", dividendExDate: "",
  // Generic fields (non-STOCK)
  value: "", annualCashFlow: "",
};

function AssetModal({ item, onClose, onSave }) {
  const [form, setForm] = useState(item ? {
    ...EMPTY_ASSET, ...item,
    totalUnits: item.totalUnits,
    dividendExDate: item.dividendExDate?.slice(0, 10) || "",
  } : { ...EMPTY_ASSET });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const t = form.assetType;

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError(null);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v != null));
    try {
      if (item?._id) await apiClient.put(`/asset-holdings/${item._id}`, payload);
      else           await apiClient.post(`/asset-holdings`, payload);
      onSave();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width: 520 }}>
        <div style={S.modalHeader}>
          <h3 style={{ margin: 0 }}>{item?._id ? "עריכת נכס" : "הוספת נכס"}</h3>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={S.form}>
          <div style={S.row}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>סוג נכס</label>
              <select style={S.input} value={t} onChange={e => set("assetType", e.target.value)}>
                {ASSET_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>מטבע</label>
              <input style={S.input} value={form.currency} onChange={e => set("currency", e.target.value)} />
            </div>
          </div>
          <label style={S.label}>שם הנכס</label>
          <input style={S.input} required value={form.assetName} onChange={e => set("assetName", e.target.value)} />

          {t === "STOCK" && <>
            <div style={S.divider}>מניות</div>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>סמל</label><input style={S.input} value={form.symbol} onChange={e => set("symbol", e.target.value)} placeholder="DLEKG.TA" /></div>
              <div style={{ flex: 1 }}><label style={S.label}>סה״כ מניות בבעלות</label><NumericInput style={S.input} min="0" required value={form.totalUnits} onChange={e => set("totalUnits", e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>מניות מוגבלות / שמורות</label><NumericInput style={S.input} min="0" value={form.minimumFreeRequired} onChange={e => set("minimumFreeRequired", e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={S.label}>סיבת ההגבלה</label><input style={S.input} value={form.minimumFreeReason} onChange={e => set("minimumFreeReason", e.target.value)} /></div>
            </div>
            <div style={S.divider}>דיבידנד</div>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>דיבידנד למניה (₪)</label><NumericInput style={S.input} min="0" value={form.dividendPerShare} onChange={e => set("dividendPerShare", e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={S.label}>תדירות</label>
                <select style={S.input} value={form.dividendFrequency} onChange={e => set("dividendFrequency", e.target.value)}>
                  {Object.entries(DIV_FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <label style={S.label}>תאריך קום-ריבית הבא</label>
            <input style={S.input} type="date" value={form.dividendExDate} onChange={e => set("dividendExDate", e.target.value)} />
          </>}

          {t !== "STOCK" && <>
            <div style={S.divider}>פרטי נכס</div>
            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>שווי (₪)</label>
                <NumericInput style={S.input} min="0" required value={form.value} onChange={e => set("value", e.target.value)} placeholder="0" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>תזרים שנתי (₪) — אופציונלי</label>
                <NumericInput style={S.input} min="0" value={form.annualCashFlow} onChange={e => set("annualCashFlow", e.target.value)} placeholder="0" />
              </div>
            </div>
          </>}

          <label style={S.label}>הערות</label>
          <textarea style={{ ...S.input, minHeight: 56, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} />
          {error && <div style={S.errorBox}>{error}</div>}
          <div style={S.modalActions}>
            <button type="button" onClick={onClose} style={S.cancelBtn}>ביטול</button>
            <button type="submit" style={S.saveBtn} disabled={saving}>{saving ? "שומר..." : "שמור"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Liability Form Modal ──────────────────────────────────────────────────────

const EMPTY_LIAB = {
  liabilityType: "OTHER", description: "", currency: "ILS",
  value: "", notes: "", status: "OPEN",
};

function LiabilityModal({ item, onClose, onSave }) {
  const [form, setForm] = useState(item ? { ...EMPTY_LIAB, ...item } : { ...EMPTY_LIAB });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError(null);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v != null));
    try {
      if (item?._id) await apiClient.put(`/liabilities/${item._id}`, payload);
      else           await apiClient.post(`/liabilities`, payload);
      onSave();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width: 440 }}>
        <div style={S.modalHeader}>
          <h3 style={{ margin: 0 }}>{item?._id ? "עריכת התחייבות" : "הוספת התחייבות"}</h3>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={S.form}>
          <div style={S.row}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>סוג</label>
              <select style={S.input} value={form.liabilityType} onChange={e => set("liabilityType", e.target.value)}>
                {LIABILITY_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>סטטוס</label>
              <select style={S.input} value={form.status} onChange={e => set("status", e.target.value)}>
                {STATUS_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          <label style={S.label}>תיאור</label>
          <input style={S.input} required value={form.description} onChange={e => set("description", e.target.value)} />

          <label style={S.label}>סכום (₪)</label>
          <NumericInput style={S.input} min="0" value={form.value} onChange={e => set("value", e.target.value)} placeholder="0" />

          <label style={S.label}>הערות</label>
          <textarea style={{ ...S.input, minHeight: 56, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} />

          {error && <div style={S.errorBox}>{error}</div>}
          <div style={S.modalActions}>
            <button type="button" onClick={onClose} style={S.cancelBtn}>ביטול</button>
            <button type="submit" style={S.saveBtn} disabled={saving}>{saving ? "שומר..." : "שמור"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────────────────────

function Section({ title, count, totalLabel, total, accentColor, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={S.sectionBox}>
      <div style={{ ...S.sectionHeader, borderColor: accentColor || "#e5e7eb" }} onClick={() => setOpen(o => !o)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {accentColor && <span style={{ width: 10, height: 10, borderRadius: 2, background: accentColor, flexShrink: 0 }} />}
          <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
          {count != null && <span style={{ fontSize: 12, color: "#9ca3af" }}>({count})</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {total != null && <span style={{ fontSize: 13, color: "#374151" }}>{totalLabel}: <strong>{fmt(total)}</strong></span>}
          <span style={{ color: "#9ca3af" }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function BalanceSheetPage({ onUnauthorized, canEdit }) {
  const user = useCurrentUser();
  const canViewAssets    = can(user, 'balanceSheet.viewAssets');
  const canViewPie       = can(user, 'balanceSheet.viewPie');
  const canViewLiab      = can(user, 'balanceSheet.viewLiabilities');
  const canViewEquity    = can(user, 'balanceSheet.viewEquity');
  // Keep asset editing available to users with the existing route-level edit
  // permission while their profile is being migrated to granular permissions.
  const canManageAssets  = can(user, 'balanceSheet.manageAssets') || canEdit;
  const canManageLiab    = can(user, 'balanceSheet.manageLiabilities');
  const [data,            setData]          = useState(null);
  const [loading,         setLoading]       = useState(true);
  const [error,           setError]         = useState(null);
  const [assetModal,      setAssetModal]    = useState(null);
  const [liabModal,       setLiabModal]     = useState(null);
  const [editingBeginning, setEditingBeginning] = useState(false);
  const [beginningInput,   setBeginningInput]   = useState("");
  const [savingBeginning,  setSavingBeginning]  = useState(false);
  const [company,          setCompany]          = useState(""); // "" = consolidated (all companies)

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiClient.get(company ? `/balance-sheet?company=${encodeURIComponent(company)}` : "/balance-sheet");
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 && onUnauthorized) onUnauthorized();
      else setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [company, onUnauthorized]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function saveBeginningBalance() {
    const val = parseFloat(beginningInput.replace(/,/g, ""));
    if (isNaN(val)) return;
    setSavingBeginning(true);
    try {
      await apiClient.put(`/system-config/beginningCashBalance`, { value: val });
      setEditingBeginning(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally { setSavingBeginning(false); }
  }

  async function deleteAsset(id) {
    if (!window.confirm("למחוק נכס זה?")) return;
    try { await apiClient.delete(`/asset-holdings/${id}`); fetchData(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  }
  async function deleteLiab(id) {
    if (!window.confirm("למחוק התחייבות זו?")) return;
    try { await apiClient.delete(`/liabilities/${id}`); fetchData(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  }

  // Pie chart data (assets side)
  const pieData = useMemo(() => {
    if (!data?.assets?.categories) return [];
    return Object.entries(data.assets.categories)
      .filter(([, g]) => g.totalValue > 0)
      .map(([type, g]) => ({ name: g.label, value: g.totalValue, type }));
  }, [data]);

  const assetCats   = data?.assets?.categories   ? Object.values(data.assets.categories)   : [];
  const liabOther   = data?.liabilities?.other ? Object.values(data.liabilities.other) : [];
  const loanLines   = data?.liabilities?.loans?.items || [];
  const netEquity   = data?.netEquity ?? null;
  const totalAssets = data?.assets?.totalAssetsValue ?? null;
  const totalLiab   = data?.liabilities?.totalLiabilities ?? null;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={{ margin: 0 }}>מאזן</h2>
          {data?.asOf && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>נכון ל-{new Date(data.asOf).toLocaleDateString("he-IL")}</div>}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ minWidth: 180 }}>
            <CompanySelect name="companyFilter" label="חברה" emptyLabel="— מאוחד —" value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
          {(canManageLiab || canManageAssets) && (
            <div style={{ display: "flex", gap: 8 }}>
              {canManageLiab && <button style={{ ...S.addBtn, background: "#7c3aed" }} onClick={() => setLiabModal(false)}>+ הוסף התחייבות</button>}
              {canManageAssets && <button style={S.addBtn} onClick={() => setAssetModal(false)}>+ הוסף נכס</button>}
            </div>
          )}
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {/* Top KPI strip */}
      {data && (
        <div style={S.kpiRow}>
          {canViewAssets && <KpiCard label="סה״כ נכסים"       value={fmt(totalAssets)} color="#1e3a5f" />}
          {canViewLiab && <KpiCard label="סה״כ התחייבויות"  value={fmt(totalLiab)}  color="#b91c1c" />}
          {canViewEquity && <KpiCard label="הון עצמי נטו"
            value={fmt(netEquity)}
            color={netEquity != null && netEquity >= 0 ? "#15803d" : "#b91c1c"}
            sub={totalAssets != null && totalLiab != null ? `${fmt(totalAssets)} פחות ${fmt(totalLiab)}` : undefined}
          />}
          {canViewAssets && <KpiCard label="הכנסה שנתית משוערת" value={fmt(data.assets?.totalAnnualIncome)} color="#15803d" />}
        </div>
      )}

      {/* Two-column layout */}
      <div style={S.twoCol}>

        {/* ── LEFT: ASSETS ──────────────────────────────────────── */}
        {canViewAssets && <div style={S.col}>
          <div style={S.colHeader}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>נכסים</span>
            <span style={{ fontWeight: 700, color: "#1e3a5f" }}>{fmt(totalAssets)}</span>
          </div>

          {/* Pie + breakdown table */}
          {canViewPie && pieData.length > 0 && (
            <div style={S.card}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: "0 0 180px" }}>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} paddingAngle={2}>
                        {pieData.map(({ type }) => <Cell key={type} fill={TYPE_COLORS[type] || "#6b7280"} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <tbody>
                      {assetCats.map(g => (
                        <tr key={g.assetType} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "5px 0" }}>
                            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: TYPE_COLORS[g.assetType] || "#6b7280", marginLeft: 6 }} />
                            {g.label}
                          </td>
                          <td style={{ textAlign: "left", fontWeight: 600, padding: "5px 0" }}>{fmt(g.totalValue)}</td>
                          <td style={{ textAlign: "left", color: "#9ca3af", padding: "5px 0" }}>
                            {totalAssets > 0 ? fmtPct((g.totalValue / totalAssets) * 100) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {loading ? <div style={S.loadingMsg}>טוען...</div> : assetCats.map(g => (
            <Section
              key={g.assetType}
              title={g.label}
              count={g.assetType === 'CASH' ? g.holdings.length - (g.computedCashIncluded === false ? 0 : 1) : g.holdings.length}
              totalLabel="שווי"
              total={g.totalValue}
              accentColor={TYPE_COLORS[g.assetType]}
            >
              {g.assetType === 'CASH' ? (
                <CashSection
                  group={g}
                  editingBeginning={editingBeginning}
                  beginningInput={beginningInput}
                  savingBeginning={savingBeginning}
                  canManage={canManageAssets}
                  onStartEdit={() => { setBeginningInput(String(g.beginningBalance ?? 0)); setEditingBeginning(true); }}
                  onCancelEdit={() => setEditingBeginning(false)}
                  onInputChange={setBeginningInput}
                  onSave={saveBeginningBalance}
                  onEditItem={canManageAssets ? item => setAssetModal(item) : null}
                  onDeleteItem={canManageAssets ? deleteAsset : null}
                />
              ) : (
                <AssetGrid group={g} onEdit={canManageAssets ? item => setAssetModal(item) : null} onDelete={canManageAssets ? deleteAsset : null} />
              )}
            </Section>
          ))}
        </div>}

        {/* ── RIGHT: LIABILITIES ────────────────────────────────── */}
        {canViewLiab && <div style={S.col}>
          <div style={S.colHeader}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>התחייבויות</span>
            <span style={{ fontWeight: 700, color: "#b91c1c" }}>{fmt(totalLiab)}</span>
          </div>

          {loading ? <div style={S.loadingMsg}>טוען...</div> : <>
            {/* Loans section */}
            <Section title="הלוואות" count={loanLines.length} totalLabel="יתרת קרן" total={data?.liabilities?.loans?.total} accentColor="#dc2626">
              {loanLines.length === 0
                ? <div style={S.emptyMsg}>אין הלוואות פעילות.</div>
                : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <th style={S.th}>מלווה</th>
                        <th style={S.th}>חברה</th>
                        <th style={{ ...S.th, textAlign: "left" }}>יתרת קרן</th>
                        <th style={S.th}>פירעון</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanLines.map(l => (
                        <tr key={l._id} style={{ borderBottom: "1px solid #f9fafb" }}>
                          <td style={S.td}><Link to={`/loan/${l._id}`} style={{ color: "#2563eb", textDecoration: "none" }}>{l.lender}</Link></td>
                          <td style={S.td}>{l.borrowerCompany}</td>
                          <td style={{ ...S.td, textAlign: "left", fontWeight: 600, color: "#b91c1c" }}>{fmt(l.remainingPrincipal)}</td>
                          <td style={S.td}>{fmtDate(l.maturityDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </Section>

            {/* Non-loan liability groups */}
            {liabOther.map(g => (
              <Section
                key={g.liabilityType}
                title={g.label}
                count={g.items.length}
                totalLabel="סה״כ"
                total={g.total}
                accentColor="#7c3aed"
              >
                <LiabilityGrid items={g.items} onEdit={canManageLiab ? item => setLiabModal(item) : null} onDelete={canManageLiab ? deleteLiab : null} />
              </Section>
            ))}

            {liabOther.length === 0 && loanLines.length > 0 && (
              <div style={{ ...S.card, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
                לא נמצאו התחייבויות נוספות. לחץ "+ הוסף התחייבות" להוסיף.
              </div>
            )}
          </>}
        </div>}
      </div>

      {/* Modals */}
      {assetModal !== null && (
        <AssetModal
          item={assetModal || null}
          onClose={() => setAssetModal(null)}
          onSave={() => { setAssetModal(null); fetchData(); }}
        />
      )}
      {liabModal !== null && (
        <LiabilityModal
          item={liabModal || null}
          onClose={() => setLiabModal(null)}
          onSave={() => { setLiabModal(null); fetchData(); }}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KpiCard({ label, value, color, sub }) {
  return (
    <div style={S.kpiCard}>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || "#1e3a5f" }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** Muted chip showing the group company that owns a holding, when set. */
function OwnerChip({ company }) {
  if (!company) return null;
  return <span style={S.ownerChip}>{company}</span>;
}

function CashSection({ group, editingBeginning, beginningInput, savingBeginning, canManage, onStartEdit, onCancelEdit, onInputChange, onSave, onEditItem, onDeleteItem }) {
  const cfNet = (group.computedCash ?? 0) - (group.beginningBalance ?? 0);
  // `isComputed` here flags the synthetic "יתרת מזומן (מחושבת)" pseudo-row the
  // /balance-sheet route injects into this group (see backend/routes/balanceSheet.js) —
  // unrelated to the enrichHoldings-level field removed elsewhere; still live here.
  const manualItems = (group.holdings || []).filter(h => !h.isComputed);
  // Business-global computed balance (and the beginning-balance it's derived from)
  // isn't attributable to a single company, so the backend omits it while filtered.
  const showComputed = group.computedCashIncluded !== false;

  const row = (label, value, opts = {}) => (
    <tr style={{ borderBottom: "1px solid #f3f4f6", ...(opts.highlight ? { background: "#f0fdf4" } : {}) }}>
      <td style={{ padding: "6px 4px", color: opts.muted ? "#6b7280" : "#111827", fontSize: 13 }}>{label}</td>
      <td style={{ padding: "6px 4px", textAlign: "left", fontWeight: opts.bold ? 700 : 400, color: opts.color || "#111827", fontSize: 13 }}>
        {value}
      </td>
      <td style={{ padding: "6px 4px", width: 80 }} />
    </tr>
  );

  return (
    <div>
      {showComputed ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {/* Beginning balance row */}
            <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "6px 4px", fontSize: 13, color: "#374151" }}>יתרת פתיחה</td>
              <td style={{ padding: "6px 4px", textAlign: "left", fontSize: 13 }}>
                {editingBeginning ? (
                  <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input
                      type="number"
                      value={beginningInput}
                      onChange={e => onInputChange(e.target.value)}
                      style={{ width: 140, padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 13 }}
                      autoFocus
                      onKeyDown={e => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancelEdit(); }}
                    />
                    <button onClick={onSave} disabled={savingBeginning} style={{ padding: "2px 10px", background: "#635bff", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>שמור</button>
                    <button onClick={onCancelEdit} style={{ padding: "2px 8px", background: "#f3f4f6", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>ביטול</button>
                  </span>
                ) : (
                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontWeight: 500 }}>{fmt(group.beginningBalance ?? 0)}</span>
                    {canManage && <button onClick={onStartEdit} style={{ padding: "1px 8px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer", fontSize: 11, color: "#374151" }}>עריכה</button>}
                  </span>
                )}
              </td>
              <td style={{ width: 80 }} />
            </tr>

            {/* CF net change */}
            {row(
              "תזרים מצטבר",
              <span style={{ color: cfNet >= 0 ? "#15803d" : "#b91c1c" }}>{cfNet >= 0 ? "+" : ""}{fmt(cfNet)}</span>,
              { muted: true }
            )}

            {/* Computed subtotal */}
            {row("יתרת מזומן (מחושבת)", fmt(group.computedCash ?? 0), { bold: true, highlight: true, color: "#1e3a5f" })}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: 11, color: "var(--text-secondary)", padding: "2px 4px 10px" }}>
          מזומן מחושב אינו כלול בסינון לפי חברה
        </div>
      )}

      {/* Manual CASH items */}
      {manualItems.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, padding: "0 4px" }}>פריטי מזומן ידניים</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {manualItems.map(h => (
                <tr key={h._id} style={{ borderBottom: "1px solid #f9fafb" }}>
                  <td style={{ padding: "5px 4px", fontSize: 13 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{h.assetName}</span>
                      <OwnerChip company={h.owningCompany} />
                    </span>
                  </td>
                  <td style={{ padding: "5px 4px", textAlign: "left", fontSize: 13, fontWeight: 500 }}>{fmt(h.balanceSheetValue)}</td>
                  <td style={{ padding: "5px 4px", width: 80, textAlign: "center" }}>
                    {onEditItem && (
                      <span style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                        <button style={S.actionBtn} onClick={() => onEditItem(h)}>עריכה</button>
                        <button style={{ ...S.actionBtn, background: "#fee2e2", color: "#b91c1c" }} onClick={() => onDeleteItem(h._id)}>מחק</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AssetGrid({ group, onEdit, onDelete }) {
  const cols = useMemo(() => {
    const t = group.assetType;
    const action = onEdit ? {
      headerName: "פעולות", width: 100, sortable: false, filter: false,
      cellRenderer: ({ data }) => (
        <div style={{ display: "flex", gap: 4 }}>
          <button style={S.actionBtn} onClick={() => onEdit(data)}>עריכה</button>
          <button style={{ ...S.actionBtn, background: "#fee2e2", color: "#b91c1c" }} onClick={() => onDelete(data._id)}>מחק</button>
        </div>
      ),
    } : null;
    const base = [{
      field: "assetName", headerName: "שם הנכס", flex: 1, minWidth: 120,
      cellRenderer: ({ value, data }) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
          <OwnerChip company={data.owningCompany} />
        </span>
      ),
    }];
    if (t === "STOCK") return [...base,
      { field: "symbol", headerName: "סמל", width: 90 },
      { headerName: "שווי שוק", width: 120, type: "numericColumn", valueGetter: ({ data }) => data.totalValue, valueFormatter: ({ value }) => fmt(value) },
      { headerName: "הכנסה שנתית", width: 120, type: "numericColumn", valueGetter: ({ data }) => data.annualIncomeEstimate, valueFormatter: ({ value }) => fmt(value) },
      ...(action ? [action] : []),
    ];
    return [...base,
      { headerName: "שווי", width: 120, type: "numericColumn", valueGetter: ({ data }) => data.balanceSheetValue, valueFormatter: ({ value }) => fmt(value) },
      { headerName: "תזרים שנתי", width: 120, type: "numericColumn", valueGetter: ({ data }) => data.annualCashFlow, valueFormatter: ({ value }) => value ? fmt(value) : "—" },
      ...(action ? [action] : []),
    ];
  }, [group.assetType, onEdit, onDelete]);

  if (!group.holdings.length) return <div style={S.emptyMsg}>אין נכסים בקטגוריה זו.</div>;
  return (
    <div className="ag-theme-alpine ag-theme-premium">
      <AgGridReact theme="legacy" rowData={group.holdings} columnDefs={cols}
        defaultColDef={{ sortable: true, filter: false, resizable: true, flex: 1, minWidth: 90 }}
        domLayout="autoHeight" enableRtl={true} />
    </div>
  );
}

function LiabilityGrid({ items, onEdit, onDelete }) {
  const cols = useMemo(() => [
    { field: "description", headerName: "תיאור", flex: 1 },
    { headerName: "סכום", width: 120, type: "numericColumn", valueGetter: ({ data }) => data.value, valueFormatter: ({ value }) => fmt(value) },
    { field: "status", headerName: "סטטוס", width: 80, valueFormatter: ({ value }) => ({ OPEN: "פתוח", SETTLED: "נסגר" }[value] || value) },
    ...(onEdit ? [{
      headerName: "פעולות", width: 100, sortable: false, filter: false,
      cellRenderer: ({ data }) => (
        <div style={{ display: "flex", gap: 4 }}>
          <button style={S.actionBtn} onClick={() => onEdit(data)}>עריכה</button>
          <button style={{ ...S.actionBtn, background: "#fee2e2", color: "#b91c1c" }} onClick={() => onDelete(data._id)}>מחק</button>
        </div>
      ),
    }] : []),
  ], [onEdit, onDelete]);

  if (!items.length) return <div style={S.emptyMsg}>אין רשומות.</div>;
  return (
    <div className="ag-theme-alpine ag-theme-premium">
      <AgGridReact theme="legacy" rowData={items} columnDefs={cols}
        defaultColDef={{ sortable: true, filter: false, resizable: true, flex: 1, minWidth: 90 }}
        domLayout="autoHeight" enableRtl={true} />
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:        { padding: 24, display: "flex", flexDirection: "column", gap: 16, direction: "rtl" },
  header:      { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  kpiRow:      { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
  kpiCard:     { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 18px" },
  twoCol:      { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" },
  col:         { display: "flex", flexDirection: "column", gap: 12 },
  colHeader:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb" },
  card:        { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14 },
  sectionBox:  { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px" },
  sectionHeader:{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", borderBottom: "2px solid", paddingBottom: 8, marginBottom: 0 },
  addBtn:      { background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13 },
  errorBox:    { background: "#fee2e2", color: "#b91c1c", padding: "10px 14px", borderRadius: 6 },
  loadingMsg:  { padding: 32, textAlign: "center", color: "#6b7280" },
  emptyMsg:    { padding: "10px 0", color: "#9ca3af", fontSize: 13 },
  actionBtn:   { background: "#eff6ff", color: "#1d4ed8", border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 11 },
  ownerChip:   { flexShrink: 0, background: "var(--bg-subtle)", color: "var(--text-secondary)", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 500 },
  th:          { padding: "5px 8px", textAlign: "right", color: "#6b7280", fontWeight: 600, fontSize: 11 },
  td:          { padding: "6px 8px", fontSize: 12 },
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal:       { background: "#fff", borderRadius: 10, padding: 24, maxHeight: "88vh", overflowY: "auto", direction: "rtl" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  form:        { display: "flex", flexDirection: "column", gap: 6 },
  row:         { display: "flex", gap: 12 },
  label:       { fontSize: 13, color: "#374151", marginTop: 6 },
  divider:     { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 14, marginBottom: 2, borderBottom: "1px solid #f3f4f6", paddingBottom: 4 },
  input:       { padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, width: "100%", boxSizing: "border-box" },
  modalActions:{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 },
  cancelBtn:   { background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  saveBtn:     { background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  closeBtn:    { background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#6b7280" },
};
